import { ResourceType } from "@common/types/auditLog";
import { HistoryField } from "@common/constants/history-source-types";

export interface HistoryActivityRelation {
    resourceType: ResourceType;
    fields: HistoryField[];
}

export interface HistoryObjectFieldOption {
    value: string;
    label: string;
    selfResourceTypes: ResourceType[];
    relations: HistoryActivityRelation[];
}

interface HistoryObjectFieldGroup {
    value: string;
    label: string;
    options: HistoryObjectFieldOption[];
}

const TASK_OBJECT_FIELD_OPTION: HistoryObjectFieldOption = {
    value: "task",
    label: "Task",
    selfResourceTypes: [ResourceType.TASK],
    relations: [],
};

const ORGANIZATION_ONLY_OBJECT_FIELD_OPTIONS: HistoryObjectFieldOption[] = [
    {
        value: "organization-member",
        label: "Member",
        selfResourceTypes: [ResourceType.ORGANIZATION_MEMBER],
        relations: [],
    },
    {
        value: "organization-invite",
        label: "Invite",
        selfResourceTypes: [ResourceType.ORGANIZATION_INVITE],
        relations: [],
    },
];

const HISTORY_OBJECT_FIELD_GROUPS: HistoryObjectFieldGroup[] = [
    {
        value: "books-and-documents",
        label: "Books & documents",
        options: [
            {
                value: "financial-account",
                label: "Financial account",
                selfResourceTypes: [ResourceType.FINANCIAL_ACCOUNT],
                relations: [],
            },
            {
                value: "chart-of-accounts",
                label: "Chart of accounts",
                selfResourceTypes: [
                    ResourceType.ACCOUNT,
                    ResourceType.ACCOUNT_GROUP,
                ],
                relations: [],
            },
            {
                value: "schedules",
                label: "Revenue recognition",
                selfResourceTypes: [
                    ResourceType.SCHEDULE_TEMPLATE,
                    ResourceType.SCHEDULE_APPLICATION,
                ],
                relations: [],
            },
            {
                value: "general-ledger",
                label: "Cash ledger",
                selfResourceTypes: [],
                relations: [
                    {
                        resourceType: ResourceType.TRANSACTION,
                        fields: [
                            HistoryField.ACCOUNT_ID,
                            HistoryField.GENERAL_LEDGER_ACCOUNT_ID,
                        ],
                    },
                ],
            },
            {
                value: "accrual-ledger",
                label: "Accrual ledger",
                selfResourceTypes: [],
                relations: [
                    {
                        resourceType: ResourceType.TRANSACTION,
                        fields: [HistoryField.ACCRUAL_ACCOUNT_ID],
                    },
                    {
                        resourceType: ResourceType.BILL,
                        fields: [HistoryField.ACCRUAL_ACCOUNT_ID],
                    },
                    {
                        resourceType: ResourceType.INVOICE,
                        fields: [HistoryField.ACCRUAL_ACCOUNT_ID],
                    },
                ],
            },
            {
                value: "transaction",
                label: "Transaction",
                selfResourceTypes: [ResourceType.TRANSACTION],
                relations: [],
            },
            {
                value: "journal-entry",
                label: "Journal entry",
                selfResourceTypes: [
                    ResourceType.JOURNAL_ENTRY,
                    ResourceType.MANUAL_JOURNAL_ENTRY,
                ],
                relations: [],
            },
            {
                value: "bill",
                label: "Bill",
                selfResourceTypes: [ResourceType.BILL],
                relations: [],
            },
            {
                value: "invoice",
                label: "Invoice",
                selfResourceTypes: [ResourceType.INVOICE],
                relations: [],
            },
            {
                value: "invoice-template",
                label: "Invoice template",
                selfResourceTypes: [ResourceType.INVOICE_TEMPLATE],
                relations: [],
            },
            {
                value: "document",
                label: "Document",
                selfResourceTypes: [ResourceType.FINANCIAL_DOCUMENT],
                relations: [
                    {
                        resourceType: ResourceType.BILL,
                        fields: [HistoryField.FINANCIAL_DOCUMENT],
                    },
                    {
                        resourceType: ResourceType.INVOICE,
                        fields: [HistoryField.FINANCIAL_DOCUMENT],
                    },
                ],
            },
        ],
    },
    {
        value: "references",
        label: "References",
        options: [
            {
                value: "entity",
                label: "Entity",
                selfResourceTypes: [ResourceType.ENTITY],
                relations: [
                    {
                        resourceType: ResourceType.TRANSACTION,
                        fields: [HistoryField.ENTITY_ID],
                    },
                    {
                        resourceType: ResourceType.FINANCIAL_ACCOUNT,
                        fields: [HistoryField.ENTITY_ID],
                    },
                    {
                        resourceType: ResourceType.FINANCIAL_DOCUMENT,
                        fields: [HistoryField.ENTITY_ID],
                    },
                    {
                        resourceType: ResourceType.TASK,
                        fields: [HistoryField.ENTITIES],
                    },
                ],
            },
            {
                value: "counterparty",
                label: "Counterparty",
                selfResourceTypes: [ResourceType.COUNTERPARTY],
                relations: [
                    {
                        resourceType: ResourceType.TRANSACTION,
                        fields: [HistoryField.COUNTERPARTY_ID],
                    },
                    {
                        resourceType: ResourceType.BILL,
                        fields: [HistoryField.COUNTERPARTY_ID],
                    },
                    {
                        resourceType: ResourceType.INVOICE,
                        fields: [HistoryField.COUNTERPARTY_ID],
                    },
                ],
            },
            {
                value: "category",
                label: "Category",
                selfResourceTypes: [ResourceType.CATEGORY],
                relations: [
                    {
                        resourceType: ResourceType.TRANSACTION,
                        fields: [HistoryField.CATEGORY_ID],
                    },
                ],
            },
            {
                value: "class",
                label: "Class",
                selfResourceTypes: [ResourceType.CLASS],
                relations: [
                    {
                        resourceType: ResourceType.TRANSACTION,
                        fields: [HistoryField.CLASS_ASSIGNMENTS],
                    },
                ],
            },
            {
                value: "catalog-item",
                label: "Catalog item",
                selfResourceTypes: [ResourceType.CATALOG_ITEM],
                relations: [],
            },
        ],
    },
    {
        value: "workflow",
        label: "Workflow",
        options: [
            TASK_OBJECT_FIELD_OPTION,
            {
                value: "rule",
                label: "Rule",
                selfResourceTypes: [
                    ResourceType.TRANSACTION_RULE,
                    ResourceType.ACCOUNTING_RULE,
                    ResourceType.TRANSFER_RULE,
                    ResourceType.SPLIT_RULE,
                ],
                relations: [],
            },
            {
                value: "transaction-review",
                label: "Transaction review",
                selfResourceTypes: [ResourceType.TRANSACTION_FIELD_REVIEW],
                relations: [],
            },
        ],
    },
];

export const CATEGORY_OBJECT_FIELD_VALUE = "category";

export const SCHEDULES_OBJECT_FIELD_VALUE = "schedules";

export const HISTORY_OBJECT_FIELD_OPTIONS: HistoryObjectFieldOption[] =
    HISTORY_OBJECT_FIELD_GROUPS.flatMap((group) => group.options);

/** Firm History only reads task, firm member and firm invite changes. */
export const ORGANIZATION_OBJECT_FIELD_OPTIONS: HistoryObjectFieldOption[] = [
    TASK_OBJECT_FIELD_OPTION,
    ...ORGANIZATION_ONLY_OBJECT_FIELD_OPTIONS,
];

/** Every page's objects, so a picked value resolves on any Activity page. */
export const ALL_OBJECT_FIELD_OPTIONS: HistoryObjectFieldOption[] = [
    ...HISTORY_OBJECT_FIELD_OPTIONS,
    ...ORGANIZATION_ONLY_OBJECT_FIELD_OPTIONS,
];
